import React, { useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import { 
  FiPlus, 
  FiTrash2, 
  FiChevronUp, 
  FiChevronDown, 
  FiEdit3, 
  FiCheck,
  FiX
} from 'react-icons/fi';

const EditableChecklist = ({ tasks = [], setTasks, title, description, iconName, colorClass, onMetaUpdate }) => {
  const [editingId, setEditingId] = useState(null);
  const [isEditingHeader, setIsEditingHeader] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const addTask = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const newTask = {
      id: `task-${Date.now()}`,
      title: 'New Task',
      description: 'Click to edit description',
      specs: 'New'
    };
    setTasks(prev => [...(prev || []), newTask]);
    setEditingId(newTask.id);
  };

  const initiateDelete = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    setConfirmDeleteId(id);
    setTimeout(() => setConfirmDeleteId(prev => prev === id ? null : prev), 3000);
  };

  const confirmDelete = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    setTasks(prev => (prev || []).filter(t => t.id !== id));
    setConfirmDeleteId(null);
  };

  const moveTask = (e, index, direction) => {
    e.preventDefault();
    e.stopPropagation();
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= tasks.length) return;
    
    setTasks(prev => {
      const newTasks = [...(prev || [])];
      const [movedTask] = newTasks.splice(index, 1);
      newTasks.splice(newIndex, 0, movedTask);
      return newTasks;
    });
  };

  const updateTask = (id, field, value) => {
    setTasks(prev => (prev || []).map(t => t.id === id ? { ...t, [field]: value } : t));
  };

  return (
    <div className="bg-white rounded-3xl shadow-sm border border-cream-dark overflow-hidden transition-all hover:shadow-md relative">
      <div className="p-6 border-b border-cream-dark bg-cream/10">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3 flex-1">
            <div className={`w-10 h-10 rounded-xl ${colorClass} flex items-center justify-center text-white shadow-sm`}>
              <SafeIcon name={iconName} className="w-5 h-5" />
            </div>
            
            {isEditingHeader ? (
              <div className="flex-1 space-y-2 animate-fade-in">
                <input 
                  autoFocus
                  className="w-full text-xl font-bold bg-transparent border-b border-terracotta focus:outline-none text-espresso"
                  value={title}
                  onChange={(e) => onMetaUpdate('title', e.target.value)}
                  onBlur={() => setIsEditingHeader(false)}
                />
              </div>
            ) : (
              <div className="flex-1 cursor-pointer group/header" onClick={() => setIsEditingHeader(true)}>
                <div className="flex items-center gap-2">
                  <h3 className="text-xl font-bold text-espresso">{title}</h3>
                  <SafeIcon icon={FiEdit3} className="w-3 h-3 text-espresso-light/20 opacity-0 group-hover/header:opacity-100 transition-opacity" />
                </div>
                <p className="text-xs text-espresso-light/60">{description}</p>
              </div>
            )}
          </div>
          
          <button 
            type="button"
            onClick={addTask}
            className="flex items-center gap-2 px-4 py-2 bg-espresso text-white rounded-xl text-xs font-bold hover:bg-espresso-light transition-all shadow-sm ml-4"
          >
            <SafeIcon icon={FiPlus} />
            Add Task
          </button>
        </div>
      </div>

      <div className="divide-y divide-cream-dark">
        {tasks.map((task, index) => (
          <div key={task.id} className="p-4 hover:bg-cream/20 transition-colors group">
            <div className="flex items-start gap-4">
              <div className="flex flex-col gap-1 mt-1">
                <button 
                  type="button"
                  disabled={index === 0}
                  onClick={(e) => moveTask(e, index, -1)}
                  className={`p-1 rounded hover:bg-cream-dark ${index === 0 ? 'opacity-20' : 'text-espresso-light'}`}
                >
                  <SafeIcon icon={FiChevronUp} className="w-3 h-3" />
                </button>
                <button 
                  type="button"
                  disabled={index === tasks.length - 1}
                  onClick={(e) => moveTask(e, index, 1)}
                  className={`p-1 rounded hover:bg-cream-dark ${index === tasks.length - 1 ? 'opacity-20' : 'text-espresso-light'}`}
                >
                  <SafeIcon icon={FiChevronDown} className="w-3 h-3" />
                </button>
              </div>
              
              <div className="flex-1">
                {editingId === task.id ? (
                  <div className="space-y-3 animate-fade-in">
                    <input 
                      autoFocus
                      className="w-full text-sm font-bold border-b border-terracotta bg-transparent focus:outline-none pb-1 text-espresso"
                      value={task.title}
                      onChange={(e) => updateTask(task.id, 'title', e.target.value)}
                    />
                    <div className="flex justify-end pt-2">
                      <button 
                        type="button"
                        onClick={() => setEditingId(null)}
                        className="text-[10px] font-bold text-sage flex items-center gap-1 hover:underline"
                      >
                        <SafeIcon icon={FiCheck} />
                        Done
                      </button>
                    </div>
                  </div>
                ) : (
                  <div onClick={() => setEditingId(task.id)} className="cursor-pointer">
                    <h4 className="font-bold text-espresso text-sm">{task.title}</h4>
                    <p className="text-xs text-espresso-light leading-relaxed">{task.description}</p>
                  </div>
                )}
              </div>
              
              {/* Task Delete Interaction */}
              <div className="flex items-center gap-2">
                {confirmDeleteId === task.id ? (
                  <div className="flex items-center gap-1 animate-fade-in">
                    <button 
                      type="button"
                      onClick={(e) => confirmDelete(e, task.id)}
                      className="bg-red-500 text-white p-1.5 rounded-lg hover:bg-red-600 transition-colors shadow-sm"
                      title="Confirm Delete"
                    >
                      <SafeIcon icon={FiCheck} className="w-3.5 h-3.5" />
                    </button>
                    <button 
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null); }}
                      className="bg-cream-dark text-espresso-light p-1.5 rounded-lg hover:bg-white transition-colors border border-cream-dark shadow-sm"
                      title="Cancel"
                    >
                      <SafeIcon icon={FiX} className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <button 
                    type="button"
                    onClick={(e) => initiateDelete(e, task.id)}
                    className="opacity-0 group-hover:opacity-100 p-2 text-espresso-light/20 hover:text-red-500 transition-all transform hover:scale-110 z-[70] relative"
                    title="Delete Task"
                  >
                    <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
        
        {tasks.length === 0 && (
          <div className="p-10 text-center text-espresso-light/20 italic text-xs">
            No tasks in this category.
          </div>
        )}
      </div>
    </div>
  );
};

export default EditableChecklist;